/**
 * How hard Needs review is, counted in memory (trial telemetry, docs/architecture/telemetry-catalog.md,
 * `review.summary`). Review actions repeat hundreds of times a session, so none of them is an event of its own: the
 * screen counts them here and sends one summary every ten active minutes and when the person leaves the job.
 *
 * Pure: no timers, no DOM. The clock is injected, and the screen calls `input()` on the person's own mouse and
 * keyboard input, so active time, the ten minutes and the stuck document are all worked out on input, never on a
 * timer. Only counts and times: never a document's id, a value or a name leaves this module (ids are only keys).
 */
import { telemetryStuckThresholds } from '../shared/telemetry-catalog';

/** A summary is due after this much active time on one job's review (and always when the person leaves it). */
export const REVIEW_SUMMARY_ACTIVE_MS = 10 * 60_000;
/**
 * The time between two inputs counts as active up to this long. Main counts a minute active while the computer
 * has been idle for under two minutes (telemetry.ts `tick`); this is the same rule, measured from the inputs.
 */
export const REVIEW_INPUT_GAP_MS = 120_000;
/** Inputs closer together than this are one: pointer moves arrive many times a second. */
export const REVIEW_INPUT_RESOLUTION_MS = 1_000;
/** A visit with no action but at least this much active time on the review is still worth a summary (they looked). */
export const REVIEW_LOOKED_MS = 15_000;

/** Why the main button didn't confirm (`review.confirm-refused:<code>`). */
export type ConfirmRefusalCode =
  'no-type' | 'not-read' | 'line-amount' | 'field-required' | 'still-reading' | 'other';

export type ReviewSummaryAttrs = {
  opened: number;
  confirmed: number;
  confirmed_unchanged: number;
  confirmed_changed: number;
  fields_changed: number;
  type_changed: number;
  skipped: number;
  back: number;
  splits: number;
  joins: number;
  pages_removed: number;
  lines_ok: number;
  refused: number;
  active_ms: number;
  median_ms_per_confirm?: number;
  waiting_start?: number;
  waiting_end?: number;
};

type Counter =
  | 'opened'
  | 'confirmed'
  | 'confirmed_unchanged'
  | 'confirmed_changed'
  | 'fields_changed'
  | 'type_changed'
  | 'skipped'
  | 'back'
  | 'splits'
  | 'joins'
  | 'pages_removed'
  | 'lines_ok'
  | 'refused';
const counters: readonly Counter[] = [
  'opened',
  'confirmed',
  'confirmed_unchanged',
  'confirmed_changed',
  'fields_changed',
  'type_changed',
  'skipped',
  'back',
  'splits',
  'joins',
  'pages_removed',
  'lines_ok',
  'refused',
];
/** Counted as something the person did (opening the first document on arrival is not, on its own). */
const actions: readonly Counter[] = counters.filter(
  (counter) =>
    counter !== 'opened' && counter !== 'confirmed_unchanged' && counter !== 'confirmed_changed',
);

/** A line item's values, as a saved line change carries them before and after. */
export type LineValues = {
  description?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  arithmeticValidated?: boolean;
};

/**
 * What a saved line change was: `changed` when a value the person sees moved (or a line was added or removed),
 * `answered` when the line's amount question was answered ("Looks right", "Use $X", or an edit that makes it add up).
 */
export function lineChange(
  before: LineValues | null,
  after: LineValues | null,
): { changed: boolean; answered: boolean } {
  if (!before || !after) return { changed: true, answered: false };
  const same = (left: unknown, right: unknown) => (left ?? null) === (right ?? null);
  const changed =
    !same(before.description, after.description) ||
    !same(before.quantity, after.quantity) ||
    !same(before.unitPrice, after.unitPrice) ||
    !same(before.amount, after.amount);
  return { changed, answered: !before.arithmeticValidated && Boolean(after.arithmeticValidated) };
}

/** The middle value (the mean of the two middle ones for an even count); null for none. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

/** A set that forgets its oldest entries past a bound, so a long session never grows it without end. */
class BoundedSet {
  private readonly items = new Set<string>();
  constructor(private readonly limit: number) {}
  has(value: string) {
    return this.items.has(value);
  }
  add(value: string) {
    this.items.delete(value);
    this.items.add(value);
    if (this.items.size > this.limit) this.items.delete(this.items.values().next().value!);
  }
}

type Shown = { id: string; activeMs: number; sinceProgressMs: number; paused: boolean };
export type ReviewInputDue = { longDocumentMinutes?: number; summaryDue?: boolean };

/** One job's review, from when it came in front until it is taken (sent) or left. */
export class ReviewSummary {
  private counts = ReviewSummary.zero();
  private activeMs = 0;
  private lastInput: number | null = null;
  private confirmMs: number[] = [];
  private waitingStart: number | null = null;
  private waitingNow: number | null = null;
  private current: Shown | null = null;
  /** Documents the person changed (a value, the type, a line, its pages): a later confirm of one is "changed". */
  private readonly changed = new BoundedSet(1_000);
  /** Documents already flagged as long, or confirmed: never flagged (again). */
  private readonly settled = new BoundedSet(1_000);
  /** A document's active time when its Confirm was pressed, until the save lands. */
  private readonly pressed = new Map<string, number>();

  constructor(private readonly now: () => number = Date.now) {}

  private static zero(): Record<Counter, number> {
    return Object.fromEntries(counters.map((counter) => [counter, 0])) as Record<Counter, number>;
  }

  /** Something was done on a document (the open one when no id is given): it is not stuck. */
  private progress(id?: string) {
    if (this.current && (id === undefined || id === this.current.id))
      this.current.sinceProgressMs = 0;
  }

  private accrue(at: number) {
    const gap =
      this.lastInput === null ? 0 : Math.max(0, Math.min(at - this.lastInput, REVIEW_INPUT_GAP_MS));
    this.lastInput = at;
    this.activeMs += gap;
    if (this.current && !this.current.paused) {
      this.current.activeMs += gap;
      this.current.sinceProgressMs += gap;
    }
  }

  /**
   * The person moved the mouse, clicked, scrolled or pressed a key on the review. Returns what is now due: a
   * document they have been on for three active minutes with nothing done (once per document), and a summary after
   * ten active minutes.
   */
  input(): ReviewInputDue {
    const at = this.now();
    if (
      this.lastInput !== null &&
      at >= this.lastInput &&
      at - this.lastInput < REVIEW_INPUT_RESOLUTION_MS
    )
      return {};
    this.accrue(at);
    const due: ReviewInputDue = {};
    const shown = this.current;
    if (
      shown &&
      !shown.paused &&
      shown.sinceProgressMs >= telemetryStuckThresholds.noProgressMs &&
      !this.settled.has(shown.id)
    ) {
      this.settled.add(shown.id);
      due.longDocumentMinutes = Math.round(shown.sinceProgressMs / 60_000);
    }
    if (this.activeMs >= REVIEW_SUMMARY_ACTIVE_MS && this.happened()) due.summaryDue = true;
    return due;
  }

  /** The window lost focus: the time up to now counts, the time away does not. */
  pause(): void {
    if (this.lastInput === null) return;
    this.accrue(this.now());
    this.lastInput = null;
  }

  /**
   * The document open as work, or null when none is (All caught up, still reading). A new document counts as
   * opened; the same one shown again does not.
   */
  shown(id: string | null): void {
    if (id === null) {
      if (this.current) this.current.paused = true;
      return;
    }
    if (this.current?.id === id) {
      this.current.paused = false;
      return;
    }
    this.counts.opened += 1;
    this.current = { id, activeMs: 0, sinceProgressMs: 0, paused: false };
  }

  /** Confirm was pressed and admitted (no refusal): its time on the document is kept until the save lands. */
  confirmPressed(id: string): void {
    this.progress(id);
    this.settled.add(id);
    if (this.current?.id === id) {
      this.pressed.set(id, this.current.activeMs);
      if (this.pressed.size > 50) this.pressed.delete(this.pressed.keys().next().value!);
    }
  }

  /** A confirmation saved. */
  confirmed(id: string): void {
    this.counts.confirmed += 1;
    if (this.changed.has(id)) this.counts.confirmed_changed += 1;
    else this.counts.confirmed_unchanged += 1;
    const ms = this.pressed.get(id);
    this.pressed.delete(id);
    if (ms !== undefined && this.confirmMs.length < 2_000) this.confirmMs.push(ms);
  }

  fieldChanged(id: string): void {
    this.counts.fields_changed += 1;
    this.changed.add(id);
    this.progress(id);
  }
  typeChanged(id: string): void {
    this.counts.type_changed += 1;
    this.changed.add(id);
    this.progress(id);
  }
  /** A saved line change: a value moved counts as a field changed; an amount question answered as a line ok. */
  lineSaved(id: string, before: LineValues | null, after: LineValues | null): void {
    const change = lineChange(before, after);
    if (change.changed) {
      this.counts.fields_changed += 1;
      this.changed.add(id);
    }
    if (change.answered) this.counts.lines_ok += 1;
    this.progress(id);
  }
  /** A split or join saved; the documents it touched count as changed. */
  boundary(kind: 'split' | 'join', ids: Iterable<string>): void {
    this.counts[kind === 'split' ? 'splits' : 'joins'] += 1;
    for (const id of ids) {
      this.changed.add(id);
      this.progress(id);
    }
  }
  pageRemoved(id: string): void {
    this.counts.pages_removed += 1;
    this.changed.add(id);
    this.progress(id);
  }
  skipped(): void {
    this.counts.skipped += 1;
  }
  back(): void {
    this.counts.back += 1;
  }
  refused(): void {
    this.counts.refused += 1;
  }

  /** Documents waiting in Needs review now: the first value of a period is its start, the last its end. */
  waiting(count: number): void {
    if (!Number.isFinite(count) || count < 0) return;
    if (this.waitingStart === null) this.waitingStart = count;
    this.waitingNow = count;
  }

  /**
   * Anything worth a summary: an action, a move to another document, or a real look at one (15 active seconds).
   * Arriving on a job and leaving at once is not.
   */
  happened(): boolean {
    return (
      actions.some((counter) => this.counts[counter] > 0) ||
      this.counts.opened >= 2 ||
      ((this.counts.opened > 0 || this.current !== null) && this.activeMs >= REVIEW_LOOKED_MS)
    );
  }

  /** The summary of the period so far, or null when nothing happened; the next period starts now either way. */
  take(): ReviewSummaryAttrs | null {
    const attrs: ReviewSummaryAttrs | null = this.happened()
      ? {
          ...this.counts,
          active_ms: Math.round(this.activeMs),
          ...(this.confirmMs.length
            ? { median_ms_per_confirm: Math.round(median(this.confirmMs)!) }
            : {}),
          ...(this.waitingStart !== null ? { waiting_start: this.waitingStart } : {}),
          ...(this.waitingNow !== null ? { waiting_end: this.waitingNow } : {}),
        }
      : null;
    this.counts = ReviewSummary.zero();
    this.activeMs = 0;
    this.confirmMs = [];
    // The next period starts with what waits now; the document still open was counted as opened in this one.
    this.waitingStart = this.waitingNow;
    return attrs;
  }
}
